import { ExperimentOutlined, SaveOutlined } from "@ant-design/icons";

// enums.js

export const baseURL = `${process.env.REACT_APP_BASE_URL}/Fintech.CBS.Backend`;
export const signalrURL = `${process.env.REACT_APP_SIGNAL_BASE_URL}/Fintech.CBS.Backend/notificationHub`;

// maximum file size for valid ID upload
export const MAX_VALID_ID_SIZE = 2;

// UserStatusEnum
export const UserStatusEnum = {
  ACTIVE: 1,
  BLOCKED: 2,
  BLACKLISTED: 3,
  INACTIVE: 4,
};

// User status badge configuration
export const STATUS_CONFIG = {
  [UserStatusEnum.ACTIVE]: { color: "success", label: "Active" },
  [UserStatusEnum.BLOCKED]: { color: "error", label: "Blocked" },
  [UserStatusEnum.BLACKLISTED]: { color: "default", label: "Blacklisted" },
  [UserStatusEnum.INACTIVE]: { color: "warning", label: "Suspended" },
};

// Remita loan request status enum
export const RemitaLoanStatusEnum = {
  Draft: 0,
  PendingApproval: 1,
  Approved: 2,
  Rejected: 3,
  Disbursed: 4,
  DisbursementFailed: 5,
  Active: 6,
  Closed: 7,
  Cancelled: 8,
};

// Remita mandate activation status enum
export const RemitaMandateActivationStatusEnum = {
  NotActivated: 0,
  ActivationPending: 1,
  Activated: 2,
  ActivationFailed: 3,
  Stopped: 4,
};

// VerificationStatusEnum
export const VerificationStatusEnum = {
  NOT_APPPLCABLE: -1,
  PENDING: 0,
  VERIFICATION_FAILED: 1,
  VERIFICATION_SUCCESSFUL: 2,
};

// UsageStatusEnum
export const UsageStatusEnum = {
  PENDING: 0,
  USED: 1,
  DISCARDED: 2,
};

// RequestStatusEnum with descriptions
export const RequestStatusEnum = {
  PENDING_VERIFICATION: {
    value: 0,
    description: "Awaiting Verification",
  },
  PENDING_PROCESSING: {
    value: 1,
    description: "Pending",
  },
  PROCESSING: {
    value: 2,
    description: "Processing",
  },
  PROCESSED: {
    value: 3,
    description: "Processed",
  },
};

export const EmploymentStatusTypeEnum = {
  UNEMPLOYED: { value: 0, description: "Unemployed" },
  SELF_EMPLOYED_SME: { value: 1, description: "Self-Employed (SME)" },
  PRIVATE_SECTOR: { value: 2, description: "Private Sector" },
  LAGOS_STATE_GOVERNMENT: { value: 3, description: "Lagos State Government" },
  OTHER_STATE_GOVERNMENTS: { value: 4, description: "Other State Governments" },
  FEDERAL_GOVERNMENT: { value: 5, description: "Federal Government" },
  RETIRED: { value: 6, description: "Retired" },
  STUDENT: { value: 7, description: "Student" },
  POLICE: { value: 8, description: "Police" },
  CUSTOM: { value: 9, description: "Custom" },
  TERTIARY: { value: 10, description: "Tertiary" },
};

export const MeansOfIdentificationEnum = {
  NATIONAL_ID: {
    value: 1,
    description: "National ID",
  },
  // INTERNATIONAL_PASSPORT: {
  //   value: 2,
  //   description: "International Passport",
  // },
  // DRIVERS_LICENSE: {
  //   value: 3,
  //   description: "Drivers License",
  // },
};

// NetworkType with description
export const NetworkTypeEnum = {
  AIRTEL: {
    value: 1,
    description: "Airtel",
  },
  // cannot start object key with a number
  MOBILE9: {
    value: 2,
    description: "9Mobile",
  },
  GLOBACOM: {
    value: 3,
    description: "Globacom",
  },
  MTN: {
    value: 4,
    description: "Mtn",
  },
  SMILE: {
    value: 5,
    description: "Smile",
  },
  NTEL: {
    value: 6,
    description: "NTel",
  },
};

// ProcessEntityType - Maps to backend enum for workflow entity types
export const ProcessEntityType = {
  NONE: 0, // Not associated with any entity
  ENTITY: 1, // Generic entity
  CONTACT: 2, // Contact/Client entity
  LOAN: 3, // Loan entity
  DEPOSIT: 4, // Deposit account entity
  INSURANCE_POLICY: 5, // Insurance policy entity
  INSURANCE_CLAIMS: 6, // Insurance claims entity
  TRANSACTION: 11, // Transaction entity

  TELLER: 13, // Teller transaction
  INWARD_TRANSACTION: 14, // Inward transaction
  SCHEDULED_PROCESS: 15, // Scheduled process (system)

  // HR / Employee entities
  EMPLOYEE_RECORD: 16, // HR - Employee Record

  // Channel entities
  CUSTOMER: 100, // Customer registration (channel)
  LOAN_REQUEST: 101, // Loan request (channel)
  TRANSFER: 102, // Transfer request (channel)
  BILLS_PAYMENT: 103, // Bills payment request (channel)
  AIRTIME_RECHARGE: 104, // Airtime recharge request (channel)
  DATA_RECHARGE: 105, // Data recharge request (channel)
  SELF_SERVICE_USER: 106, // Self service user (channel)

  // Operations
  RECONCILIATION: 200, // Reconciliation (operations)

  EMPLOYEE_RECORD: 16, // HR - Employee Record

  // Disbursement
  DISBURSEMENT_BATCH: 300, // Operations - Disbursement Batch
  DISBURSEMENT: 301, // Operations - Disbursement

  // NIBSS Mandates
  NIBSS_MANDATE_BATCH: 310, // NIBSS - Mandate Batch
  NIBSS_MANDATE: 311, // NIBSS - Mandate

  // NIBSS Collections
  NIBSS_COLLECTION_BATCH: 320, // NIBSS - Collection Batch
  NIBSS_COLLECTION: 321, // NIBSS - Collection

  // Remita
  REMITA_LOAN_REQUEST: 330, // Remita - Loan Request
  REMITA_COLLECTION_BATCH: 331, // Remita - Collection Batch
  REMITA_COLLECTION: 332, // Remita - Collection
};

// Helper to get entity type label
export const getEntityTypeLabel = (entityType) => {
  const labels = {
    [ProcessEntityType.NONE]: "None",
    [ProcessEntityType.ENTITY]: "Entity",
    [ProcessEntityType.CONTACT]: "Contact",
    [ProcessEntityType.LOAN]: "Loan",
    [ProcessEntityType.DEPOSIT]: "Deposit",
    [ProcessEntityType.INSURANCE_POLICY]: "Insurance Policy",
    [ProcessEntityType.INSURANCE_CLAIMS]: "Insurance Claims",
    [ProcessEntityType.TRANSACTION]: "Transaction",

    [ProcessEntityType.TELLER]: "Teller",
    [ProcessEntityType.INWARD_TRANSACTION]: "Inward Transaction",
    [ProcessEntityType.SCHEDULED_PROCESS]: "Scheduled Process",

    [ProcessEntityType.EMPLOYEE_RECORD]: "HR - Employee Record",

    [ProcessEntityType.CUSTOMER]: "Customer",
    [ProcessEntityType.LOAN_REQUEST]: "Loan Request",
    [ProcessEntityType.TRANSFER]: "Transfer",
    [ProcessEntityType.BILLS_PAYMENT]: "Bills Payment",
    [ProcessEntityType.AIRTIME_RECHARGE]: "Airtime Recharge",
    [ProcessEntityType.DATA_RECHARGE]: "Data Recharge",
    [ProcessEntityType.SELF_SERVICE_USER]: "Self Service User",

    [ProcessEntityType.RECONCILIATION]: "Reconciliation",
    [ProcessEntityType.EMPLOYEE_RECORD]: "Employee Record",
    [ProcessEntityType.DISBURSEMENT_BATCH]: "Operations - Disbursement Batch",
    [ProcessEntityType.DISBURSEMENT]: "Operations - Disbursement",

    [ProcessEntityType.NIBSS_MANDATE_BATCH]: "NIBSS - Mandate Batch",
    [ProcessEntityType.NIBSS_MANDATE]: "NIBSS - Mandate",

    [ProcessEntityType.NIBSS_COLLECTION_BATCH]: "NIBSS - Collection Batch",
    [ProcessEntityType.NIBSS_COLLECTION]: "NIBSS - Collection",

    [ProcessEntityType.REMITA_LOAN_REQUEST]: "Remita - Loan Request",
    [ProcessEntityType.REMITA_COLLECTION_BATCH]: "Remita - Collection Batch",
    [ProcessEntityType.REMITA_COLLECTION]: "Remita - Collection",
  };

  return labels[entityType] || "Unknown";
};

export const LoanStatusEnum = {
  PENDING_PROCESSING: {
    value: 0,
    description: "Pending Processing",
  },
  PENDING_APPROVAL: {
    value: 1,
    description: "Pending Approval",
  },
  PARTIAL_APPROVED: {
    value: 2,
    description: "Partial Approved",
  },
  FULLY_APPROVED: {
    value: 3,
    description: "Fully Approved",
  },
  REJECTED: {
    value: 4,
    description: "Rejected",
  },
  RETURNED: {
    value: 5,
    description: "Returned",
  },
};

export const LoanDisbursementStatusEnum = {
  NOT_INITIATED: {
    value: 0,
    description: "Not Initiated",
  },
  PENDING_DISBURSEMENT: {
    value: 1,
    description: "Pending Disbursement",
  },
  PROCESSING_DISBURSEMENT: {
    value: 2,
    description: "Processing Disbursement",
  },
  DISBURSEMENT_SUCCESSFUL: {
    value: 3,
    description: "Disbursed Successfully",
  },
  DISBURSEMENT_FAILED: {
    value: 4,
    description: "Disbursement Failed",
  },
  DISBURSEMENT_CANCELLED: {
    value: 5,
    description: "Disbursement Cancelled",
  },
};
export const LoanSearchStatusEnum = {
  PENDING: {
    value: 0,
    description: "Pending",
  },
  SUCCESSFUL: {
    value: 1,
    description: "Successful",
  },
  FAILED: {
    value: 2,
    description: "Failed",
  },
  NOT_APPLICABLE: {
    value: 3,
    description: "Not Applicable",
  },
};

export const LoanInterestRateCalculationEnum = {
  DAILY: {
    value: 0,
    description: "Daily",
  },

  WEEKLY: {
    value: 1,
    description: "Weekly",
  },
  MONTHLY: {
    value: 2,
    description: "Monthly",
  },
  YEARLY: {
    value: 3,
    description: "Yearly",
  },
};

// `value` is the integer the backend understands (used when sending to / reading from the API).
// `stringValue` is what the loan product configuration (GetConfigQuery) returns.
export const loanDeductionCalculationEnum = {
  FLAT: {
    value: 0,
    stringValue: "flat",
    description: "Flat Amount",
  },
  PERCENTAGE_ON_LOAN_AMOUNT: {
    value: 1,
    stringValue: "percentageLoanAmount",
    description: "Percentage (Loan Amount)",
  },
  PERCENTAGE_ON_DISBURSED_AMOUNT: {
    value: 2,
    stringValue: "percentageDisbursedAmount",
    description: "Percentage (Disbursed Amount)",
  },
};

export const SettlementOptionEnum = {
  VIRTUAL_ACCOUNT: {
    value: 0,
    description: "Virtual Account",
  },
  DIRECT_SETTLEMENT_ACCOUNT: {
    value: 1,
    description: "Direct Settlement Account",
  },
};

// TransactionType with description
export const TransactionTypeEnum = {
  ALL: {
    value: "",
    description: "All",
  },
  AIRTIME: {
    value: "AIRTIME RECHARGE",
    description: "Airtime",
  },
  BILLS: {
    value: "BILLS PAYMENT",
    description: "Bills",
  },
  DATA: {
    value: "DATA RECHARGE",
    description: "Data",
  },
  TRANSFER: {
    value: "TRANSFER",
    description: "Transfer",
  },
};

// TransactionStatus with description
export const TransactionStatusEnum = {
  ALL: {
    value: "",
    description: "All",
  },
  INITIATED: {
    value: 0,
    description: "Initiated",
  },
  PENDING: {
    value: 1,
    description: "Pending",
  },
  PROCESSING: {
    value: 2,
    description: "Processing",
  },
  FAILED: {
    value: 3,
    description: "Failed",
  },
  SUCCESSFUL: {
    value: 4,
    description: "Successful",
  },
  CANCELED: {
    value: 5,
    description: "Canceled",
  },

  DECLINED: {
    value: 6,
    description: "Declined",
  },
  REVERSED: {
    value: 7,
    description: "Reversed",
  },
  REVERSAL_FAILED: {
    value: 8,
    description: "Reversal Failed",
  },
};

// SavingsType with description
export const SelfServiceSavingsPlanTypeEnum = {
  FIXED: {
    value: 0,
    description: "Fixed Savings Plan",
  },
  FLEXI: {
    value: 1,
    description: "Flexi Savings Plan",
  },
  TARGET: {
    value: 2,
    description: "Target Savings Plan",
  },
};

// SavingsFrequency with description
export const SelfServiceSavingsFrequencyEnum = {
  FIXED: {
    value: 0,
    description: "Fixed",
  },
  DAILY: {
    value: 1,
    description: "Daily",
  },
  WEEKLY: {
    value: 2,
    description: "Weekly",
  },
  MONTHLY: {
    value: 3,
    description: "Monthly",
  },
  BIMONTHLY: {
    value: 5,
    description: "BiMonthly",
  },
};

export const savingsPlanStatusEnum = {
  PENDING: {
    value: 0,
    description: "Pending",
  },
  CLOSE: {
    value: 1,
    description: "Close",
  },
  FAILED: {
    value: 3,
    description: "Failed",
  },
  ACTIVE: {
    value: 4,
    description: "Active",
  },
};

// savingsDuration with frequency
export const SAVINGS_DURATION_OPTIONS = {
  [SelfServiceSavingsPlanTypeEnum.TARGET.value]: {
    [SelfServiceSavingsFrequencyEnum.DAILY.value]: [
      { value: 7, label: "7 days" },
      { value: 14, label: "14 days" },
      { value: 30, label: "30 days" },
      { value: 60, label: "60 days" },
      { value: 90, label: "90 days" },
      { value: 120, label: "120 days" },
      { value: 150, label: "150 days" },
      { value: 180, label: "180 days" },
      { value: 210, label: "210 days" },
      { value: 240, label: "240 days" },
      { value: 270, label: "270 days" },
      { value: 300, label: "300 days" },
      { value: 330, label: "330 days" },
      { value: 360, label: "360 days" },
    ],
    [SelfServiceSavingsFrequencyEnum.WEEKLY.value]: [
      { value: 1, label: "1 week" },
      { value: 2, label: "2 weeks" },
      { value: 3, label: "3 weeks" },
      { value: 4, label: "4 weeks" },
      { value: 8, label: "8 weeks" },
      { value: 12, label: "12 weeks" },
      { value: 16, label: "16 weeks" },
      { value: 20, label: "20 weeks" },
      { value: 24, label: "24 weeks" },
      { value: 28, label: "28 weeks" },
      { value: 32, label: "32 weeks" },
      { value: 36, label: "36 weeks" },
      { value: 40, label: "40 weeks" },
      { value: 44, label: "44 weeks" },
      { value: 48, label: "48 weeks" },
      { value: 52, label: "52 weeks" },
    ],
    [SelfServiceSavingsFrequencyEnum.MONTHLY.value]: [
      { value: 1, label: "1 month" },
      { value: 2, label: "2 months" },
      { value: 3, label: "3 months" },
      { value: 4, label: "4 months" },
      { value: 5, label: "5 months" },
      { value: 6, label: "6 months" },
      { value: 7, label: "7 months" },
      { value: 8, label: "8 months" },
      { value: 9, label: "9 months" },
      { value: 10, label: "10 months" },
      { value: 11, label: "11 months" },
      { value: 12, label: "12 months" },
    ],
    [SelfServiceSavingsFrequencyEnum.BIMONTHLY.value]: [
      { value: 2, label: "Every 2 Weeks for 1 Month" },
      { value: 3, label: "Every 2 Weeks for 1.5 Months" },
      { value: 4, label: "Every 2 Weeks for 2 Months" },
      { value: 5, label: "Every 2 Weeks for 2.5 Months" },
      { value: 6, label: "Every 2 Weeks for 3 Months" },
      { value: 7, label: "Every 2 Weeks for 3.5 Months" },
      { value: 8, label: "Every 2 Weeks for 4 Months" },
      { value: 9, label: "Every 2 Weeks for 4.5 Months" },
      { value: 10, label: "Every 2 Weeks for 5 Months" },
      { value: 11, label: "Every 2 Weeks for 5.5 Months" },
      { value: 12, label: "Every 2 Weeks for 6 Months" },
    ],
  },
  [SelfServiceSavingsPlanTypeEnum.FIXED.value]: {
    [SelfServiceSavingsFrequencyEnum.DAILY.value]: [
      { value: 30, label: "30 days" },
      { value: 60, label: "60 days" },
      { value: 90, label: "90 days" },
      { value: 180, label: "180 days" },
      { value: 270, label: "270 days" },
      { value: 300, label: "300 days" },
      { value: 360, label: "360 days" },
    ],
  },
};

export const LimitTypeEnum = {
  TRANSACTION_LIMIT: {
    value: 0,
    description: "Transaction Limit",
  },
  DAILY_LIMIT: {
    value: 1,
    description: "Daily Limit",
  },
};

export const LimitTransactionType = {
  AIRTIME: {
    value: 0,
    description: "Airtime",
  },
  DATA: {
    value: 1,
    description: "Data",
  },
  BILL: {
    value: 2,
    description: "Bills",
  },
  TRANSFER: {
    value: 3,
    description: "Transfer",
  },
};

// SelfUserClassificationEnum with descriptions
export const SelfUserClassificationEnum = {
  CUSTOMER: {
    value: 0,
    description: "Customer",
  },
  CORPORATE_USERS: {
    value: 1,
    description: "Corporate Users",
  },
  BACKOFFICE_ADMINISTRATORS: {
    value: 2,
    description: "Administrator",
  },
};

// Loan referral source (matches backend ReferredByType enum)
export const ReferralTypeEnum = {
  NONE: { value: 0, label: "None" },
  STAFF: { value: 1, label: "Staff" },
  FRIEND: { value: 2, label: "Friend" },
  ADVERT: { value: 3, label: "Advert" },
};

// Loan tenor unit (matches backend LoanTenorType enum)
export const LoanTenorTypeEnum = {
  DAYS: { value: 0, label: "Day(s)" },
  WEEKS: { value: 1, label: "Week(s)" },
  MONTHS: { value: 2, label: "Month(s)" },
  YEARS: { value: 3, label: "Year(s)" },
};

// OTP Verification Method Enum (matches backend VerificationMethodType)
export const OtpVerificationMethodEnum = {
  DEFAULT: {
    value: 0,
    description: "Default",
    icon: "safety",
  },
  EMAIL: {
    value: 1,
    description: "Email",
    icon: "mail",
  },
  SMS: {
    value: 2,
    description: "SMS",
    icon: "mobile",
  },
  AUTHENTICATOR_APP: {
    value: 3,
    description: "Authenticator App",
    icon: "safety",
  },
};

export const ProductCategoryEnum = {
  ALL: {
    value: 0,
    description: "All",
  },
  DEPOSIT: {
    value: 1,
    description: "Deposit",
  },
  LOAN: {
    value: 2,
    description: "Loan",
  },
  INSURANCE: {
    value: 3,
    description: "Insurance",
  },
};

// SelfServiceAccountTypeEnum with descriptions
export const SelfServiceAccountTypeEnum = {
  NONE: {
    value: 0,
    description: "Backoffice Users",
  },
  INDIVIDUAL: {
    value: 1,
    description: "Individual",
  },
  CORPORATE: {
    value: 2,
    description: "Corporate",
  },
};
export const SelfServiceDisbursementMode = {
  NONE: {
    value: 0,
    description: "Not Set",
  },
  MANUAL: {
    value: 1,
    description: "Manual",
  },
  BANK_TRANSFER: {
    value: 2,
    description: "Bank Transfer",
  },
};

export const SelfServiceLoanCollectionMode = {
  NONE: {
    value: 0,
    description: "Not Set",
  },
  MANUAL: {
    value: 1,
    description: "Manual",
  },
  NIBBS_DIRECT_DEBIT: {
    value: 2,
    description: "NIBSS Direct Debit",
  },
};

export const depositAccountStatusEnum = {
  ALL: {
    value: 0,
    description: "All",
  },
  PARTIAL_APPLICATION: {
    value: 1,
    description: "Partial Application",
  },
  PENDING_APPROVAL: {
    value: 2,
    description: "Pending Approval",
  },
  APPROVED: {
    value: 3,
    description: "Approved",
  },
  REJECTED: {
    value: 4,
    description: "Rejected",
  },
  ACTIVE: {
    value: 5,
    description: "Active",
  },
  IN_AREARS: {
    value: 6,
    description: "In Arears",
  },
  CLOSED: {
    value: 7,
    description: "Closed",
  },
  CLOSED_WRITTEN_OFF: {
    value: 8,
    description: "Closed Written Off",
  },
  DORMANT: {
    value: 9,
    description: "Dormant",
  },
  LOCKED: {
    value: 10,
    description: "Locked",
  },
  MATURED: {
    value: 11,
    description: "Matured",
  },
  WITHDRAWN: {
    value: 12,
    description: "Withdrawn",
  },
};

export const loanAccountStatusEnum = {
  PARTIAL_APPLICATION: {
    value: 1,
    description: "Partial Application",
  },
  PENDING_APPROVAL: {
    value: 2,
    description: "Pending Approval",
  },
  APPROVED: {
    value: 3,
    description: "Approved",
  },
  REJECTED: {
    value: 4,
    description: "Rejected",
  },
  ACTIVE: {
    value: 5,
    description: "Active",
  },
  IN_AREARS: {
    value: 6,
    description: "In Arears",
  },
  CLOSED: {
    value: 7,
    description: "Closed",
  },
  CLOSED_WRITTEN_OFF: {
    value: 8,
    description: "Closed Written Off",
  },
  CLOSED_WITHDRAWN: {
    value: 9,
    description: "Closed Withdrawn",
  },
};

export const clientStateEnum = {
  ALL: {
    value: 0,
    description: "All",
  },
  ACTIVE: {
    value: 1,
    description: "Active",
  },
  PENDING_APPROVAL: {
    value: 3,
    description: "Pending Approval",
  },
  APPROVED: {
    value: 4,
    description: "Approved",
  },
  REJECTED: {
    value: 5,
    description: "Rejected",
  },
  BLACKLISTED: {
    value: 6,
    description: "BlackListed",
  },
  EXITED: {
    value: 7,
    description: "Exited",
  },
};

export const VirtualAccountCreationModeEnum = {
  AUTOMATIC_CREATION: {
    value: 0,
    description: "Automatic Creation",
  },
  EXISTING_SETTLEMENT_ACCOUNT: {
    value: 1,
    description: "Existing Settlement Account",
  },
};

export const VirtualAccountLinkingTypeEnum = {
  INTERNAL_BANKING_PROFILE: {
    value: 0,
    description: "Internet Banking Profile",
  },
  CORE_BANKING_PROFILE: {
    value: 1,
    description: "Core Banking Profile",
  },
};

export const TillAccountStateEnum = {
  OPENED: {
    value: 1,
    description: "Opened",
  },
  CLOSED: {
    value: 2,
    description: "Closed",
  },
  DELETED: {
    value: 3,
    description: "Deleted",
  },
};

export const TillTypeEnum = {
  TELLER_TILL: {
    value: 0,
    description: "Teller Till",
  },
  VAULT: {
    value: 1,
    description: "Vault",
  },
};

export const TelleringSearchEnum = {
  DEPOSIT: {
    value: 0,
    description: "Deposit Account",
  },
  LOAN: {
    value: 1,
    description: "Loan Account",
  },
};

// cashorCheque (enum): Indicates whether the withdrawal is via cash(0) or cheque(1).
export const CashOrChequeEnum = {
  CASH: {
    value: 0,
    description: "Cash",
  },
  CHEQUE: {
    value: 1,
    description: "Cheque",
  },
};

export const CBSTransactionHoldStateEnum = {
  PENDING: {
    value: 1,
    description: "Pending",
  },
  REVERSED: {
    value: 2,
    description: "Reversed",
  },
  SETTLED: {
    value: 3,
    description: "Settled",
  },
  EXPIRED: {
    value: 4,
    description: "Expired",
  },
  CANCELLED: {
    value: 5,
    description: "Cancelled",
  },
  HOLD: {
    value: 6,
    description: "Hold",
  },
  SUSPEND: {
    value: 7,
    description: "Suspend",
  },
};

export const CoreTransactionTypeEnum = {
  DEPOSIT: {
    value: 0,
    description: "Deposit",
  },
  WITHDRAWAL: {
    value: 1,
    description: "Withdrawal",
  },
  TRANSFER: {
    value: 2,
    description: "Transfer",
  },
  TELLER_DEPOSIT: {
    value: 3,
    description: "Teller Deposit",
  },
  TELLER_WITHDRAWAL: {
    value: 4,
    description: "Teller Withdrawal",
  },
};

export const CoreTransactionCrossReferenceEnum = {
  DEPOSIT: {
    value: 1,
    description: "Deposit Account",
  },
  WITHDRAWAL: {
    value: 2,
    description: "Loan Account",
  },
};

export const DocumentTypeEnum = {
  NATIONAL_ID: {
    value: 1,
    description: "National ID card",
  },
  INTERNATIONAL_PASSPORT: {
    value: 2,
    description: "Passport",
  },
  DRIVERS_LICENCE: {
    value: 3,
    description: "Driver's license",
  },
  UTILITY_BILL: {
    value: 4,
    description: "Utility bill for address verification",
  },
  BANK_STATEMENT: {
    value: 5,
    description: "Bank statement",
  },
  COMPANY_REGISTRATION: {
    value: 6,
    description: "Company registration certificate",
  },
  TAX_CLEARANCE: {
    value: 7,
    description: "Tax clearance certificate",
  },
  CERTIFICATE_OF_INCORPORATION: {
    value: 8,
    description: "Certificate of incorporation",
  },
  PHOTOGRAPH: {
    value: 14,
    description: "Customer photograph",
  },
  UNIVERSITY_CERTIFICATE: {
    value: 15,
    description: "Educational certificate",
  },
  BIRTH_CERTIFICATE: {
    value: 16,
    description: "Birth certificate",
  },
};

export const CoreClientClassificationEnum = {
  INDIVIDUAL: {
    value: 0,
    description: "Individual",
  },
  GROUP: {
    value: 1,
    description: "Group",
  },
};

export const Relationship = [
  "Spouse",
  "Father",
  "Mother",
  "Son",
  "Daughter",
  "Brother",
  "Sister",
  "Cousin",
  "Nephew",
  "Niece",
  "Guardian",
  "Relative",
  "Friend",
  "Other",
];

export const downloadFileType = {
  PDF: "application/pdf",
  EXCEL: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  PNG: "image/png",
  JPEG: "image/jpeg",
  JPG: "image/jpeg",
  WEBP: "image/webp",
  DOC: "application/msword",
  DOCX: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
};

export const MONTH_DAYS_WITH_ORDINALS = [
  { value: 1, label: "1st" },
  { value: 2, label: "2nd" },
  { value: 3, label: "3rd" },
  { value: 4, label: "4th" },
  { value: 5, label: "5th" },
  { value: 6, label: "6th" },
  { value: 7, label: "7th" },
  { value: 8, label: "8th" },
  { value: 9, label: "9th" },
  { value: 10, label: "10th" },
  { value: 11, label: "11th" },
  { value: 12, label: "12th" },
  { value: 13, label: "13th" },
  { value: 14, label: "14th" },
  { value: 15, label: "15th" },
  { value: 16, label: "16th" },
  { value: 17, label: "17th" },
  { value: 18, label: "18th" },
  { value: 19, label: "19th" },
  { value: 20, label: "20th" },
  { value: 21, label: "21st" },
  { value: 22, label: "22nd" },
  { value: 23, label: "23rd" },
  { value: 24, label: "24th" },
  { value: 25, label: "25th" },
  { value: 26, label: "26th" },
  { value: 27, label: "27th" },
  { value: 28, label: "28th" },
  { value: 29, label: "29th" },
  { value: 30, label: "30th" },
  { value: 31, label: "31st" },
];

export const tablePageSizeOptions = ["10", "25", "50", "100"];

export const featuresEnum = Object.freeze({
  CUSTOMER_PORTAL: -100,
  ADMIN_PORTAL: -200,
  CUSTOMER_AIRTIME_RECHARGE: 0,
  CUSTOMER_DATA_PURCHASE: 1,
  CUSTOMER_BILL: 2,
  CUSTOMER_TRANSFER: 3,
  CUSTOMER_LOAN: 4,
  CUSTOMER_INSURANCE: 5,
  CUSTOMER_SAVINGS: 6,
  CUSTOMER_INVESTMENT: 7,
  CUSTOMER_LIMIT_UPDATE: 8,
  CUSTOMER_ACCOUNT_UPGRADE: 9,
  CUSTOMER_SIGNUP_CORPORATE: 10,
  CUSTOMER_SIGNUP_INDIVIDUAL: 11,
  PORTAL_SUPPORT_MODAL: 12,
  PORTAL_LANGUAGE_TOGGLE: 13,
  PORTAL_DARK_MODE_SWITCH: 14,
  PORTAL_CALL_BACK_REQUEST: 15,
  CUSTOMER_ACCOUNT_STATMENT: 16,
  CUSTOMER_TRANSACTIONS: 17,
  CUSTOMER_SAVINGS_FEATURE: 19,

  ADMIN_USER_MANAGEMENT: 2000,
  ADMIN_ROLE_MANAGEMENT: 2001,
  ADMIN_LOAN_REQUESTS: 2002,
  ADMIN_INSURANCE_REQUESTS: 2003,
  ADMIN_SAVINGS_REQUESTS: 2004,
  ADMIN_TRANSFER_REQUESTS: 2005,
  ADMIN_BILL_REQUESTS: 2006,
  ADMIN_AIRTIME_REQUESTS: 2007,
  ADMIN_DATA_REQUESTS: 2008,
  ADMIN_LIMIT: 2009,
  ADMIN_NIBSS_MANDATE_SETUP: 2010,
  ADMIN_NIBSS_MANDATE_COLLECTION: 2011,
  ADMIN_PRODUCT_CONFIGURATION: 2012,
  ADMIN_MANAGE_CHANNELS: 2013,
  ADMIN_CUSTOMERS_REPORT: 2014,
  ADMIN_TRANSACTIONS_REPORT: 2015,
  ADMIN_GENERAL_SEARCH: 2016,
  ADMIN_ORGANISATION_SETUP: 2017,
  ADMIN_PROFILE_LINKING: 2018,
  ADMIN_SETUP_SAVINGS_PLAN: 2019,

  DEVELOPER_TOOL: 5000,
  DEVELOPER_TOOL_COMMAND_CHAINS: 5001,
  DEVELOPER_TOOL_TABLES: 5002,
  DEVELOPER_TOOL_VIEWS: 5003,
  DEVELOPER_TOOL_LANGUAGES: 5004,
  DEVELOPER_TOOL_HTML_TEMPLATES: 5005,
  DEVELOPER_TOOL_FILE_TEMPLATES: 5006,
  DEVELOPER_TOOL_CONFIG: 5007,
  DEVELOPER_TOOL_PRODUCT_SETUP: 5008,
  DEVELOPER_TOOL_PRODUCT_INSURANCE_SETUP: 5009,
  DEVELOPER_TOOL_PRODUCT_LOAN_SETUP: 5010,
  DEVELOPER_TOOL_PRODUCT_DEPOSIT_SETUP: 5011,
  DEVELOPER_TOOL_CATALOG: 5012,
});

// Feature toggle codes returned from the platform / used by hasFeature
export const FeatureCodeEnum = Object.freeze({
  CUSTOMER_PORTAL: "CUSTOMER_PORTAL",
  CUSTOMER_ACCOUNT_STATEMENT: "CUSTOMER_ACCOUNT_STATEMENT",
  CUSTOMER_TRANSACTIONS: "CUSTOMER_TRANSACTIONS",
  CUSTOMER_AIRTIME_RECHARGE: "CUSTOMER_AIRTIME_RECHARGE",
  CUSTOMER_DATA_PURCHASE: "CUSTOMER_DATA_PURCHASE",
  CUSTOMER_BILL: "CUSTOMER_BILL",
  CUSTOMER_TRANSFER: "CUSTOMER_TRANSFER",
  CUSTOMER_LOAN: "CUSTOMER_LOAN",
  CUSTOMER_INSURANCE: "CUSTOMER_INSURANCE",
  CUSTOMER_SAVINGS: "CUSTOMER_SAVINGS",
  CUSTOMER_INVESTMENT: "CUSTOMER_INVESTMENT",
  CUSTOMER_LIMIT_UPDATE: "CUSTOMER_LIMIT_UPDATE",
  CUSTOMER_ACCOUNT_UPGRADE: "CUSTOMER_ACCOUNT_UPGRADE",
  CUSTOMER_SUBACCOUNTS: "CUSTOMER_SUBACCOUNTS",
});

export const MODULE_CODE_TO_FEATURE_FLAG = Object.freeze({
  AdminCustomers: "ADMIN_MODULE_CUSTOMERS",
  AdminPolicies: "ADMIN_MODULE_POLICIES",
  AdminDepositManagement: "ADMIN_MODULE_DEPOSIT_MANAGEMENT",
  AdminLendingManagement: "ADMIN_MODULE_LENDING_MANAGEMENT",
  AdminTransactions: "ADMIN_MODULE_TRANSACTIONS",
  AdminAgencyBanking: "ADMIN_MODULE_AGENCY_BANKING",
  AdminManageChannels: "ADMIN_MODULE_MANAGE_CHANNELS",
  AdminManageOperations: "ADMIN_MODULE_MANAGE_OPERATIONS",
  AdminTellering: "ADMIN_MODULE_TELLERING",
  AdminSIL: "ADMIN_MODULE_SIL",
  AdminAccounting: "ADMIN_MODULE_ACCOUNTING",
  AdminSettings: "ADMIN_MODULE_SETTINGS",
  Administrations: "ADMIN_MODULE_ADMINISTRATIONS",
  AdminSIL: "ADMIN_MODULE_SIL",
});

export const PATH_TO_FEATURE_FLAG = Object.freeze({
  "/create-branch": "ADMIN_ORGANISATION_SETUP",
  "/branches": "ADMIN_ORGANISATION_SETUP",

  "/administrative-users": "ADMIN_USER_MANAGEMENT",
  "/new-user": "ADMIN_USER_MANAGEMENT",

  "/roles": "ADMIN_ROLE_MANAGEMENT",
  "/new-role": "ADMIN_ROLE_MANAGEMENT",

  "/create-policy": "ADMIN_INSURANCE_REQUESTS",
  "/Policies": "ADMIN_INSURANCE_REQUESTS",

  // Claims
  "/new-claim": "ADMIN_INSURANCE_REQUESTS",
  "/admin-claims": "ADMIN_INSURANCE_REQUESTS",

  // BPM / Developer Tools
  "/Process": "DEVELOPER_TOOL",
  "/object-definitions": "DEVELOPER_TOOL",
  "/business-rules": "DEVELOPER_TOOL",
  "/rule-table": "DEVELOPER_TOOL_TABLES",
  "/forms": "DEVELOPER_TOOL",
  "/process-launcher": "DEVELOPER_TOOL",
  "/chains": "DEVELOPER_TOOL_COMMAND_CHAINS",
  "/Views": "DEVELOPER_TOOL_VIEWS",
  "/acturial-sheets": "DEVELOPER_TOOL",

  // General Search
  "/general-search": "ADMIN_GENERAL_SEARCH",

  // Mandates
  "/mandate-batches-reports": "ADMIN_NIBSS_MANDATE_COLLECTION",
  "/mandate-reports": "ADMIN_NIBSS_MANDATE_COLLECTION",
  "/create-mandate": "ADMIN_NIBSS_MANDATE_SETUP",
  "/mandate/upload-mandate": "ADMIN_NIBSS_MANDATE_SETUP",

  // Collections
  "/collection/reports": "ADMIN_NIBSS_MANDATE_COLLECTION",
  "/mandate/collection-report": "ADMIN_NIBSS_MANDATE_COLLECTION",
  "/collection/initiate-batch-form": "ADMIN_NIBSS_MANDATE_COLLECTION",
  "/collection/initiate-single-form": "ADMIN_NIBSS_MANDATE_COLLECTION",

  "/financial-products": "DEVELOPER_TOOL_PRODUCT_DEPOSIT_SETUP",
  "/insurance": "DEVELOPER_TOOL_PRODUCT_INSURANCE_SETUP",
  "/loan-products": "DEVELOPER_TOOL_PRODUCT_LOAN_SETUP",
  "/document-template": "DEVELOPER_TOOL_FILE_TEMPLATES",
  "/html-templates": "DEVELOPER_TOOL_HTML_TEMPLATES",

  // Manage Channels Reports
  "/customers-report": "ADMIN_CUSTOMERS_REPORT",
  "/loan-requests-report": "ADMIN_LOAN_REQUESTS",
  "/transactions-report": "ADMIN_TRANSACTIONS_REPORT",
  "/transfers-report": "ADMIN_TRANSFER_REQUESTS",
  "/liveness-check": "ADMIN_LIVENESS_CHECK",

  "/mandate/selector": "ADMIN_NIBSS_MANDATE_SETUP",
  "/general-requests-selector": "ADMIN_MANAGE_CHANNELS",
  "/organisations": "ADMIN_ORGANISATION_SETUP",

  // ===== Appended mappings (post-initial block): feature flags for previously-unmapped menus =====
  "/contact-lists": "ADMIN_CONTACTS",
  "/new-contact": "ADMIN_CONTACTS",
  "/communications": "ADMIN_CONTACTS",

  "/coa-management": "ADMIN_CHART_OF_ACCOUNTS",
  "/chart-of-accounts": "ADMIN_CHART_OF_ACCOUNTS",

  "/deposit-transactions": "ADMIN_DEPOSIT_OPERATIONS",
  "/deposit-fund": "ADMIN_DEPOSIT_OPERATIONS",
  "/withdraw-fund": "ADMIN_DEPOSIT_OPERATIONS",
  "/transfer-fund": "ADMIN_DEPOSIT_OPERATIONS",

  "/loan-transactions": "ADMIN_LOAN_OPERATIONS",
  "/loan-repayment": "ADMIN_LOAN_OPERATIONS",

  "/audit-logs": "ADMIN_AUDIT_LOG",
  "/system-logs": "ADMIN_SYSTEM_LOG",
  "/languages": "ADMIN_LANGUAGE",
  "/platform-configuration": "ADMIN_PLATFORM_CONFIG",
  "/general-config": "ADMIN_GENERAL_CONFIG",
  "/recurring-jobs": "ADMIN_RECURRING_JOBS",
  "/webhooks": "ADMIN_WEBHOOKS",
  "/secure-credentials": "ADMIN_SECURE_CREDENTIALS",

  "/create-virtual-account": "ADMIN_VIRTUAL_ACCOUNT_MANAGEMENT",
  "/virtual-accounts": "ADMIN_VIRTUAL_ACCOUNT_MANAGEMENT",
  "/new-terminal": "ADMIN_TERMINAL_MANAGEMENT",
  "/terminals": "ADMIN_TERMINAL_MANAGEMENT",
  "/create-merchant": "ADMIN_MERCHANT_MANAGEMENT",
  "/merchant-lists": "ADMIN_MERCHANT_MANAGEMENT",
  "/journals": "ADMIN_JOURNALS",
  "/journals-entry": "ADMIN_CREATE_JOURNAL_ENTRY",

  // deposit account management
  "/deposits": "ADMIN_DEPOSIT_ACCOUNT_MANAGEMENT",
  "/new-deposit": "ADMIN_DEPOSIT_ACCOUNT_MANAGEMENT",

  // loan account management
  "/all-loans": "ADMIN_LOAN_ACCOUNT_MANAGEMENT",
  "/new-loan": "ADMIN_LOAN_ACCOUNT_MANAGEMENT",
  "/schema": "DEVELOPER_TOOL_SCHEMA",

  // SIL (Stand In Ledger)
  "/sil-tellering-dashboard": "ADMIN_SIL_TELLERING_DASHBOARD",
  "/sil-till-transactions": "ADMIN_SIL_TILL_TRANSACTIONS",
  "/sil-customer-accounts": "ADMIN_SIL_CUSTOMER_ACCOUNTS",
});

export const PermissionsEnum = Object.freeze({
  // Customer Permissions
  CUS_DASH: "CUS_DASH",
  CUS_BILL_INIT: "CUS_BILL_INIT",
  CUS_AIRTIME_INIT: "CUS_AIRTIME_INIT",
  CUS_DATA_INIT: "CUS_DATA_INIT",
  CUS_TRANSFER_INIT: "CUS_TRANSFER_INIT",
  CUS_STATEMENT_INIT: "CUS_STATEMENT_INIT",
  CUS_BILL_VERIFY: "CUS_BILL_VERIFY",
  CUS_AIRTIME_VERIFY: "CUS_AIRTIME_VERIFY",
  CUS_DATA_VERIFY: "CUS_DATA_VERIFY",
  CUS_TRANSFER_VERIFY: "CUS_TRANSFER_VERIFY",
  CUS_STATEMENT_VERIFY: "CUS_STATEMENT_VERIFY",

  // Admin Permissions
  ADM_LOAN_REQUESTAPPROVAL: "ADM_LOAN_REQUESTAPPROVAL",
  ADM_LOAN_APPROVE: "ADM_LOAN_APPROVE",
  ADM_LOAN_REJECT: "ADM_LOAN_REJECT",
  ADM_LOAN_RETURN: "ADM_LOAN_RETURN",
  ADM_LOAN_WITHDRAW: "ADM_LOAN_WITHDRAW",
  ADM_LOAN_VIEW: "ADM_LOAN_VIEW",
  ADM_CUSTOMER_VIEW: "ADM_CUSTOMER_VIEW",
  ADM_DASH: "ADM_DASH",
  ADM_SEARCH: "ADM_SEARCH",
  ADM_REPORT_CUST: "ADM_REPORT_CUST",
  ADM_REPORT_LOAN: "ADM_REPORT_LOAN",
  ADM_REPORT_TRANS: "ADM_REPORT_TRANS",
  ADM_REPORT_TRANSFER: "ADM_REPORT_TRANSFER",
  ADM_EXPORT_CUST: "ADM_EXPORT_CUST",
  ADM_EXPORT_LOAN: "ADM_EXPORT_LOAN",
  ADM_EXPORT_TRANS: "ADM_EXPORT_TRANS",
  ADM_EXPORT_TRANSFER: "ADM_EXPORT_TRANSFER",
  ADM_CONFIG: "ADM_CONFIG",
  ADM_LIMIT_MGMT: "ADM_LIMIT_MGMT",
  ADM_LIMIT_REQ: "ADM_LIMIT_REQ",
  ADM_PROVIDER: "ADM_PROVIDER",
  ADM_REACTIVATE: "ADM_REACTIVATE",
  ADM_MANDATE_VIEW_BATCH: "ADM_MANDATE_VIEW_BATCH",
  ADM_MANDATE_UPLOAD_BATCH: "ADM_MANDATE_UPLOAD_BATCH",
  ADM_MANDATE_SINGLE: "ADM_MANDATE_SINGLE",
  ADM_MANDATE_VIEW: "ADM_MANDATE_VIEW",
  ADM_MANDATE_EXPORT: "ADM_MANDATE_EXPORT",
  ADM_COLLECTION_VIEW_BATCH: "ADM_COLLECTION_VIEW_BATCH",
  ADM_COLLECTION_UPLOAD_BATCH: "ADM_COLLECTION_UPLOAD_BATCH",
  ADM_COLLECTION_SINGLE: "ADM_COLLECTION_SINGLE",
  ADM_COLLECTION_EXPORT: "ADM_COLLECTION_EXPORT",
  ADM_COLLECTION_TEMPLATE: "ADM_COLLECTION_TEMPLATE",
  ADM_ORG_MGMT: "ADM_ORG_MGMT",
  ADM_DIV_MGMT: "ADM_DIV_MGMT",
  ADM_AREA_MGMT: "ADM_AREA_MGMT",
  ADM_BRANCH_MGMT: "ADM_BRANCH_MGMT",
  ADM_USER_NEW: "ADM_USER_NEW",
  ADM_USER_VIEW: "ADM_USER_VIEW",
  ADM_USER_EDIT: "ADM_USER_EDIT",
  ADM_USER_SUSPEND: "ADM_USER_SUSPEND",
  ADM_USER_BLACKLIST: "ADM_USER_BLACKLIST",
  ADM_USER_ACTIVATE: "ADM_USER_ACTIVATE",
  ADM_USER_DEACTIVATE: "ADM_USER_DEACTIVATE",
  ADM_NOTIFICATIONS: "ADM_NOTIFICATIONS",
  // new permissions - 16/01/2025
  ADM_MANDATE_UPDATE: "ADM_MANDATE_UPDATE",
  ADM_MANDATE_REFRESH: "ADM_MANDATE_REFRESH",
  ADM_MANDATE_START: "ADM_MANDATE_START",
  ADM_MANDATE_STOP: "ADM_MANDATE_STOP",
  ADM_MANDATE_AUTHORIZE: "ADM_MANDATE_AUTHORIZE",
  ADM_MANDATE_DECLINE: "ADM_MANDATE_DECLINE",
  ADM_COLLECTIONBATCH_AUTHORIZE: "ADM_COLLECTIONBATCH_AUTHORIZE",
  ADM_COLLECTIONBATCH_DEACTIVATE: "ADM_COLLECTIONBATCH_DEACTIVATE",
  ADM_COLLECTIONBATCH_CANCEL: "ADM_COLLECTIONBATCH_CANCEL",
  ADM_COLLECTIONBATCH_COLLECTFUNDS: "ADM_COLLECTIONBATCH_COLLECTFUNDS",
  ADM_COLLECTIONBATCH_COLLECTFUNDS_SINGLE:
    "ADM_COLLECTIONBATCH_COLLECTFUNDS_SINGLE",
});

export const PERMISSION_CODES = Object.freeze({
  USER_MANAGEMENT: {
    CREATE_USER: "bnk_create_user",
    EDIT_USER: "bnk_edit_user",
    CREATE_ROLES: "bnk_create_roles",
    MANAGE_ROLE: "bnk_manage_role",
    VIEW_USERS: "bnk_view_users",
    VIEW_ROLES_AND_PERMISSION: "bnk_view_roles_and_permission",
    EXPORT_USERS: "bnk_export_users",
    EXPORT_ROLES: "bnk_export_roles",
  },
  LOAN_MANAGEMENT: {
    CREATE_LOAN: "bnk_create_loan",
    REQUEST_LOAN_APPROVAL: "bnk_request_loan_approval",
    EDIT_LOAN_ACCOUNT: "bnk_edit_loan_account",
    VIEW_LOAN_ACCOUNTS: "bnk_view_loan_accounts",
    APPROVE_LOAN_ACCOUNT: "bnk_approve_loan_account",
    REJECT_LOAN_ACCOUNT: "bnk_reject_loan_account",
    VIEW_LOAN_COMMUNICATIONS: "bnk_view_loan_communications",
    VIEW_LOAN_COMMENTS: "bnk_view_loan_comments",
    VIEW_LOAN_ATTACHMENTS: "bnk_view_loan_attachments",
    VIEW_LOAN_ACTIVITIES: "bnk_view_loan_activities",
    MANAGE_LOAN_ATTACHMENTS: "bnk_manage_loan_attachments",
    MANAGE_LOAN_COMMENTS: "bnk_manage_loan_comments",
    RESCHEDULE_LOAN: "bnk_reschedule_loan_account",
    WRITEOFF_LOAN: "bnk_writeoff_loan_account",
    PAYOFF_LOAN: "bnk_payoff_loan_account",
    REFINANCE_LOAN: "bnk_refinance_loan_account",
    VIEW_LOAN_TRANSACTIONS: "bnk_view_loan_transactions",
    APPLY_REPAYMENT: "bnk_apply_loan_repayment",
    REVERSE_LOAN_TRANSACTION: "bnk_reverse_loan_transaction",
    LOAN_LOCK_UNLOCK_ACCOUNT: "bnk_loan_lock_account",
    TRIGGER_LOAN_NOTIFICATION: "bnk_trigger_loan_notification",
    SEND_LOAN_OFFERLETTER: "bnk_send_loan_offerletter",
    UPDATE_LOAN_SETTLEMENT: "bnk_manage_loan_update_settlement",
    APPLY_LOAN_FEES: "bnk_apply_loan_fees",
    MANAGE_LOAN_VISIBILITY: "bnk_manage_loan_visibility",
    APPROVE_LOAN_ACCOUNT_2NDLEVEL: "bnk_approve_loan_2ndlevel_account",
    DISBURSE_LOAN_ACCOUNT: "bnk_disburse_loan_account",
  },

  CLIENT_MANAGEMENT: {
    CREATE_CLIENT: "bnk_create_client",
    EDIT_CLIENT: "bnk_edit_client",
    APPROVE_CLIENT: "bnk_approve_client",
    REQUEST_CLIENT_APPROVAL: "bnk_request_client_approval",
    BLACKLIST_CUSTOMER: "bnk_blacklist_customer",
    VIEW_CLIENTS: "bnk_view_clients",
    MANAGE_CLIENTS_TASK: "bnk_manage_clients_task",
    VIEW_CLIENT_COMMUNICATIONS: "bnk_view_client_communications",
    VIEW_CUSTOMER_DOCUMENTS: "bnk_view_customer_documents",
    VIEW_CUSTOMER_COMMENTS: "bnk_view_customer_comments",
    MANAGE_CLIENTS_ATTACHMENT: "bnk_manage_clients_attachment",
    MANAGE_CLIENTS_COMMENTS: "bnk_manage_clients_comments",
    EDIT_CLIENT_GROUP: "bnk_edit_client_group",
    PATCHCUSTOMER_CONTACT: "bnk_patchcustomer_contact",
    PATCHCUSTOMER_RISKLEVEL: "bnk_patchcustomer_risklevel",
    PATCHCUSTOMER_ADDRESS: "bnk_patchcustomer_address",
    PATCHCUSTOMER_ASSOCIATION: "bnk_patchcustomer_association",
    PATCHCUSTOMER_BASICINFO: "bnk_patchcustomer_basicinfo",
    VIEW_CLIENT_GROUPS: "bnk_view_client_groups",
    MANAGE_GROUP_MEMBERS: "bnk_view_manage_group_members",
    UPLOAD_CLIENT_SIGNATURE: "bnk_manage_clients_upload_signature",
    UPLOAD_CLIENT_PHOTO: "bnk_manage_clients_upload_photo",
  },

  DEPOSIT_MANAGEMENT: {
    CREATE_DEPOSIT: "bnk_create_deposit",
    REQUEST_DEPOSIT_APPROVAL: "bnk_request_deposit_approval",
    EDIT_DEPOSIT_ACCOUNT: "bnk_edit_deposit_account",
    VIEW_DEPOSIT_ACCOUNTS: "bnk_view_deposit_accounts",
    APPROVE_DEPOSIT_ACCOUNT: "bnk_approve_deposit_account",
    REJECT_DEPOSIT_ACCOUNT: "bnk_reject_deposit_account",
    VIEW_DEPOSIT_COMMUNICATIONS: "bnk_view_deposit_communications",
    VIEW_DEPOSIT_COMMENTS: "bnk_view_deposit_comments",
    VIEW_DEPOSIT_ATTACHMENTS: "bnk_view_deposit_attachments",
    VIEW_DEPOSIT_ACTIVITIES: "bnk_view_deposit_activities",
    MANAGE_DEPOSIT_ATTACHMENTS: "bnk_manage_deposit_attachments",
    MANAGE_DEPOSIT_COMMENTS: "bnk_manage_deposit_comments",
    VIEW_DEPOSIT_TRANSACTIONS: "bnk_view_deposit_transactions",

    MANAGE_PLATFORM_ACCESS: "bnk_manage_platform_access",
    VIEW_DEPOSIT_ACCOUNTS_AS_OFFICER: "bnk_view_deposit_accounts_acc_officer",
    VIEW_DEPOSIT_ACCOUNTS_DATA_ENTRY: "bnk_view_deposit_accounts_data_entry",
    POST_WITHDRAWAL: "bnk_deposit_post_withdrawal",
    POST_DEPOSIT: "bnk_deposit_post_deposit",
    MAKE_TRANSFER: "bnk_deposit_make_transfer",
    LOCK_UNLOCK_ACCOUNT: "bnk_deposit_lock_account",
    LOCK_UNLOCK_AMOUNT: "bnk_deposit_lock_amount",
    APPLY_DEPOSIT_FEES: "bnk_apply_deposit_fees",
    REVERSE_DEPOSIT_TRANSACTIONS: "bnk_reverse_deposit_transactions",
    MANAGE_ALERT_NOTIFICATION: "bnk_manage_alert_notification",
    VIEW_DEPOSIT_FEE_APPLICATIONS: "bnk_view_deposit_fee_applications",
    VIEW_DEPOSIT_INTEREST_APPLICATIONS:
      "bnk_view_deposit_interest_applications",
    CLOSE_DEPOSIT_ACCOUNT: "bnk_close_deposit_account",
    MANAGE_DEPOSIT_VISIBILITY: "bnk_manage_deposit_visibility",
    MANAGE_DEPOSIT_SETTLEMENT: "bnk_manage_deposit_settlement",
    VIEW_AUTHORIZATION_HOLDS: "bnk_view_authorization_holds",
    VIEW_PENDING_TRANSACTIONS: "bnk_view_pending_transactions",
    VIEW_GL_TRANSACTIONS: "bnk_view_gl_transactions",
  },

  CHEQUE_MANAGEMENT: {
    VIEW_CHEQUE_CLEARING: "bnk_view_cheque_clearing",
    CANCEL_CHEQUE_CLEARING: "bnk_cancel_cheque_clearing",
    CLEAR_CHEQUE_CLEARING: "bnk_clear_cheque_clearing",
    VIEW_UNCLEARED_CHEQUES: "bnk_view_uncleared_cheques",
    VIEW_CHEQUE_TRANSACTIONS: "bnk_view_cheque_transactions",
  },

  ACCOUNTING: {
    VIEW_CHARTS_OF_ACCOUNTS: "bnk_view_charts_of_accounts",
    CREATE_CHARTS_OF_ACCOUNTS: "bnk_create_charts_of_accounts",
    EDIT_CHARTS_OF_ACCOUNTS: "bnk_edit_charts_of_accounts",
    CREATE_JOURNAL_ENTRIES: "bnk_create_journal_entries",
    REVERSE_GL_TRANSACTION: "bnk_reverse_gl_transaction",
    VIEW_BALANCE_SHEET: "bnk_view_balance_sheet",
    VIEW_PROFIT_AND_LOSS: "bnk_view_profit_and_loss",
    VIEW_TRIAL_BALANCE: "bnk_view_trial_balance",
    VIEW_JOURNAL_ENTRIES: "bnk_view_journal_entries",
    VIEW_INTERBRANCH_CONTROLS: "bnk_view_interbank_controls",
    MANAGE_INTERBRANCH_CONTROLS: "bnk_manage_interbank_controls",
  },

  TELLER_MANAGEMENT: {
    VIEW_TELLER_DASHBOARD: "bnk_view_teller_dashboard",
    VIEW_TELLER_TRANSACTIONS: "bnk_view_teller_transactions",
    VIEW_TELLER_TILLS: "bnk_view_teller_tills",
    VIEW_TELLER_VAULTS: "bnk_view_teller_vaults",
    VIEW_TELLER_VAULTS_TRANSACTIONS: "bnk_view_teller_vaults_transactions",
    APPROVE_PENDING_TRANSACTION: "bnk_view_approve_pending_transaction",
    REJECT_PENDING_TRANSACTION: "bnk_view_reject_pending_transaction",
  },

  BRANCH_MANAGEMENT: {
    MANAGE_BRANCH_CLOSURE: "bnk_manage_branch_closure",
    VIEW_BRANCHES: "bnk_view_branches",
    VIEW_BRANCHES_CLOSURE: "bnk_view_branches",
    EDIT_BRANCHES_CLOSURE: "bnk_view_branches",
    CREATE_BRANCH: "bnk_create_branch",
    EDIT_BRANCH: "bnk_edit_branch",
    EXPORT_BRANCHES: "bnk_export_branches",
  },

  CLIENT_OFFER_MANAGEMENT: {
    CLIENT_ACCEPT_LOAN_OFFER: "bnk_client_accept_loan_offer",
    CLIENT_REJECT_LOAN_OFFER: "bnk_client_reject_loan_offer",
  },

  MANDATE_MANAGEMENT: {
    SETUP_MANDATE: "Mandate (NDD)/SetupMandate",
    UPLOAD_MANDATES: "Mandate (NDD)/UploadMandates",
    AUTHORISE_MANDATE: "Mandate (NDD)/AuthoriseMandate",
    MANAGE_MANDATES: "Mandate (NDD)/ManageMandates",
    VIEW_MANDATES: "Mandate (NDD)/ViewMandates",
    CREATE_UPLOAD_NEW_SCHEDULE: "Mandate (NDD)/CreateUploadNewSchedule",
    MANAGE_SCHEDULE_BATCH: "Mandate (NDD)/ManageScheduleBatch",
    MANAGE_MANDATE_SCHEDULE: "Mandate (NDD)/ManageMandateSchedule",
    INITIATE_COLLECTION: "Mandate (NDD)/InitiateCollection",
    VIEW_MANDATE_SCHEDULES: "Mandate (NDD)/ViewMandateSchedules",
  },

  REMITA_SALARY_LOAN: {
    PROCESS_LOAN_REQUEST: "Salary Loan (Remita)/ProcessLoanRequest",
    DISBURSE_LOAN_REQUEST: "Salary Loan (Remita)/DisburseLoanRequest",
    MANAGE_LOAN_REQUEST: "Salary Loan (Remita)/ManageLoanRequest",
    VIEW_LOAN_REQUESTS: "Salary Loan (Remita)/ViewLoanRequests",
    ACTIVATE_REMITA_MANDATE: "Salary Loan (Remita)/ActivateRemitaMandate",
    SET_PROCESSING_COMPANY: "Salary Loan (Remita)/SetProcessingCompany",
    MANAGE_REMITA_MANDATE: "Salary Loan (Remita)/ManageRemitaMandate",
  },

  PLATFORM_MANAGEMENT: {
    ACCESS_PLATFORM_MENU: "Platform/AccessPlatformMenu",
    COMPANY_WHITELIST: "Platform/CompanyWhiteList",
    CUSTOMER_WHITELIST: "Platform/CustomerWhiteList",
    PAYROLL_INFORMATION: "Platform/PayrollInformation",
    ONBOARDING_REPORT: "Platform/OnboardingReport",
    PAYMENT_REPORT: "Platform/PaymentReport",
    TRANSFERS_REPORT: "Platform/TransfersReport",
    LOAN_REQUESTS_REPORT: "Platform/LoanRequestsReport",
  },

  INDEX_RATE_MANAGEMENT: {
    CREATE_CLIENT_GROUP: "bnk_create_client_group",
    CREATE_INDEX_RATE: "bnk_create_index_rate",
    VIEW_INDEX_RATES: "bnk_view_index_rates",
    EDIT_INDEX_RATES: "bnk_edit_index_rates",
    ADJUST_OVERDRAFT: "DepositAdjustOverdraft",
  },

  DASHBOARD: {
    VIEW_GENERAL_INDICATORS: "Dashboard/ViewGeneralIndicators",
    VIEW_ACTIVITIES: "Dashboard/ViewActivities",
    VIEW_VAULT_BALANCES: "Dashboard/ViewVaultBalances",
    VIEW_LOAN_PORTFOLIO: "Dashboard/ViewLoanPortfolio",
    VIEW_DEPOSIT_BALANCE: "Dashboard/ViewDepositBalance",
  },
  CHANNEL_CUSTOMERS: {
    GENERAL_SEARCH: "chn_general_search",
    VIEW_CUSTOMERS: "chn_view_customers",
    EXPORT_CUSTOMERS: "chn_export_customers",
  },

  CHANNEL_TRANSFERS: {
    VIEW_TRANSFERS: "chn_view_transfers",
    EXPORT_TRANSFERS: "chn_export_transfers",
  },

  CHANNEL_TRANSACTIONS: {
    VIEW_TRANSACTIONS: "chn_view_transactions",
    EXPORT_TRANSACTIONS: "chn_export_transactions",
  },

  CHANNEL_LOAN_REQUESTS: {
    VIEW_LOAN_REQUESTS: "chn_view_loan_requests",
    EXPORT_LOAN_REQUESTS: "chn_export_loan_requests",
    INITIATE_LOAN_APPROVAL: "chn_initiate_loan_approval",
    RETURN_LOAN_REQUEST: "chn_return_loan_request",
    APPROVE_LOAN_REQUEST: "chn_approve_loan_request",
    REJECT_LOAN_REQUEST: "chn_reject_loan_request",
    DISBURSE_LOAN: "chn_disburse_loan",
    UPDATE_LOAN_INTEREST_RATE: "chn_update_loan_interest_rate",
    SEND_LOAN_OFFER: "chn_send_loan_offer",
    RECOMMEND_LOAN_AMOUNT: "chn_recommend_loan_amount",
    CONFIGURE_LOAN_DISBURSEMENT: "chn_configure_loan_disbursement",
    CONFIGURE_LOAN_COLLECTION: "chn_configure_loan_collection",
    VIEW_LOAN_ACTIVITIES: "chn_view_loan_activities",
    VIEW_LOAN_DOCUMENTS: "chn_view_loan_documents",
    WAIVE_LOAN_REPAYMENT: "chn_waive_loan_repayment",
    MOVE_LOAN_BRANCH: "chn_move_loan_branch",
  },

  CHANNEL_MANDATES_MANAGEMENT: {
    VIEW_MANDATES: "chn_view_mandates",
    CREATE_MANDATE: "chn_create_mandate",
    UPLOAD_MANDATE: "chn_upload_mandate",
    EXPORT_MANDATES: "chn_export_mandates",
    VIEW_MANDATE_BATCHES: "chn_view_mandate_batches",
    EXPORT_MANDATE_BATCHES: "chn_export_mandate_batches",
  },

  CHANNEL_COLLECTIONS_MANAGEMENT: {
    VIEW_COLLECTIONS: "chn_view_collections",
    CREATE_COLLECTION_BATCH: "chn_create_collection_batch",
    CREATE_SINGLE_COLLECTION: "chn_create_single_collection",
    EXPORT_COLLECTIONS: "chn_export_collections",
  },

  CHANNEL_ORGANISATIONS: {
    VIEW_ORGANISATIONS: "chn_view_organisations",
    MANAGE_ORGANISATIONS: "chn_manage_organisations",
    VIEW_DIVISIONS: "chn_view_divisions",
    MANAGE_DIVISIONS: "chn_manage_divisions",
    VIEW_AREAS: "chn_view_areas",
    MANAGE_AREAS: "chn_manage_areas",
    VIEW_BRANCHES: "chn_view_branches",
    MANAGE_BRANCHES: "chn_manage_branches",
  },

  CHANNEL_GENERAL_REQUESTS: {
    MANAGE_LIMITS: "chn_manage_limits",
    VIEW_LIMIT_REQUESTS: "chn_view_limit_requests",
    MANAGE_PROVIDERS: "chn_manage_providers",
    VIEW_ACCOUNT_REACTIVATIONS: "chn_view_account_reactivations",
  },

  BPM_MANAGEMENT: {
    DEVELOPER_TOOLS_MANAGER: "dev_tools_manager",
    VIEW_PROCESS_LAUNCHER: "dev_view_process_launcher",
    LAUNCH_PROCESS: "dev_launch_process",
    VIEW_PROCESS_INSTANCES: "dev_view_process_instances",
    VIEW_PROCESS_TESTING: "dev_view_process_testing",
    VIEW_BUSINESS_RULES: "dev_view_business_rules",
    MANAGE_BUSINESS_RULES: "dev_manage_business_rules",
    EXPORT_BUSINESS_RULES: "dev_export_business_rules",
    VIEW_RULE_TABLES: "dev_view_rule_tables",
    MANAGE_RULE_TABLES: "dev_manage_rule_tables",
    EXPORT_RULE_TABLES: "dev_export_rule_tables",
    VIEW_FORMS: "dev_view_forms",
    MANAGE_FORMS: "dev_manage_forms",
    VIEW_COMMAND_CHAINS: "dev_view_command_chains",
    MANAGE_COMMAND_CHAINS: "dev_manage_command_chains",
    VIEW_CUSTOM_VIEWS: "dev_view_views",
    MANAGE_CUSTOM_VIEWS: "dev_manage_views",
    VIEW_ACTUARIAL_SHEETS: "dev_view_actuarial_sheets",
    EXPORT_ACTUARIAL_SHEETS: "dev_export_actuarial_sheets",
  },

  PRODUCT_WORKSHOP_MANAGEMENT: {
    VIEW_FINANCIAL_PRODUCTS: "dev_view_financial_products",
    MANAGE_FINANCIAL_PRODUCTS: "dev_manage_financial_products",
    VIEW_INSURANCE_PRODUCTS: "dev_view_insurance_products",
    MANAGE_INSURANCE_PRODUCTS: "dev_manage_insurance_products",
    VIEW_LOAN_PRODUCTS: "dev_view_loan_products",
    MANAGE_LOAN_PRODUCTS: "dev_manage_loan_products",
    VIEW_DOCUMENT_TEMPLATES: "dev_view_document_templates",
    MANAGE_DOCUMENT_TEMPLATES: "dev_manage_document_templates",
    VIEW_HTML_TEMPLATES: "dev_view_html_templates",
    MANAGE_HTML_TEMPLATES: "dev_manage_html_templates",
    VIEW_OBJECT_DEFINITIONS: "dev_view_object_definitions",
    MANAGE_OBJECT_DEFINITIONS: "dev_manage_object_definitions",
  },

  SETTINGS: {
    VIEW_AUDIT_LOGS: "dev_view_audit_logs",
    MANAGE_AUDIT_LOGS: "dev_manage_audit_logs",
    VIEW_SYSTEM_LOGS: "dev_view_system_logs",
    MANAGE_SYSTEM_LOGS: "dev_manage_system_logs",
    VIEW_SCHEMA: "dev_view_schema",
    MANAGE_SCHEMA: "dev_manage_schema",
    VIEW_LANGUAGES: "dev_view_languages",
    MANAGE_LANGUAGES: "dev_manage_languages",
    VIEW_PLATFORM_CONFIG: "dev_view_platform_config",
    MANAGE_PLATFORM_CONFIG: "dev_manage_platform_config",
    VIEW_GENERAL_CONFIG: "dev_view_general_config",
    MANAGE_GENERAL_CONFIG: "dev_manage_general_config",
    VIEW_RECURRING_JOBS: "dev_view_recurring_jobs",
    MANAGE_RECURRING_JOBS: "dev_manage_recurring_jobs",
    VIEW_WEBHOOKS: "dev_view_webhooks",
    MANAGE_WEBHOOKS: "dev_manage_webhooks",
    VIEW_SECURE_CREDENTIALS: "dev_view_secure_credentials",
    MANAGE_SECURE_CREDENTIALS: "dev_manage_secure_credentials",
  },

  AGENCY_BANKING: {
    VIEW_MERCHANTS: "agc_view_merchants",
    CREATE_MERCHANT: "agc_create_merchant",
    EDIT_MERCHANT: "agc_edit_merchant",
    VIEW_TERMINALS: "agc_view_terminals",
    CREATE_TERMINAL: "agc_create_terminal",
    EDIT_TERMINAL: "agc_edit_terminal",
    VIEW_PTSP_TRANSACTIONS: "agc_view_ptsp_transactions",
    CONFIGURE_PTSP: "agc_configure_ptsp",
    VIEW_VIRTUAL_ACCOUNTS: "agc_view_virtual_accounts",
    CREATE_VIRTUAL_ACCOUNT: "agc_create_virtual_account",
    EDIT_VIRTUAL_ACCOUNT: "agc_edit_virtual_account",
    CONFIGURE_VIRTUAL_ACCOUNT: "agc_configure_virtual_account",
  },
  OPERATIONS: {
    VIEW_OUTWARD_TRANSACTIONS: "bnk_view_outward_transactions",
    VIEW_INWARD_TRANSACTIONS: "bnk_view_inward_transactions",
    VIEW_DATA_INGESTION: "bnk_view_data_ingestion",
    EVALUATE_ALERT: "bnk_evaluate_alert",
    VIEW_ALERT_MONITORING: "bnk_view_alert_monitoring",
    VIEW_RECONCILIATION: "bnk_view_reconciliation",
    CONFIGURE_RECONCILIATION: "bnk_configure_reconciliation",
    CONFIGURE_TRANSACTION_MONITORING: "bnk_configure_transaction_monitoring",
    VIEW_BOOKS: "bnk_view_books",
    MANAGE_BOOKS: "bnk_manage_books",
    VIEW_EMPLOYEES: "bnk_view_employees",
    MANAGE_EMPLOYEES: "bnk_manage_employees",
    VIEW_REFERRALS: "bnk_view_referrals",
  },

  INSURANCE: {
    VIEW_POLICIES: "bnk_view_policies",
    CREATE_POLICY: "bnk_create_policy",
    EDIT_POLICY: "bnk_edit_policy",
    VIEW_CLAIMS: "bnk_view_claims",
    CREATE_CLAIM: "bnk_create_claim",
    EDIT_CLAIM: "bnk_edit_claim",
  },
});

export const MandateStatus = {
  Draft: { value: 0, desc: "New Mandate Initiated by Biller" },
  NewMandateInitiated: { value: 1, desc: "New Mandate Initiated by Biller" },
  MandateAuthorisedByBiller: { value: 2, desc: "Mandate Authorized by Biller" },
  MandateRejectedByBiller: { value: 3, desc: "Mandate Rejected by Biller" },
  MandateApprovedByBiller: { value: 4, desc: "Mandate Approved by Biller" },
  MandateDisApprovedByBiller: {
    value: 5,
    desc: "Mandate Disapproved by Biller",
  },
  MandateAuthorisedByBank: { value: 6, desc: "Mandate Authorised by Bank" },
  MandateRejectedByBank: { value: 7, desc: "Mandate Rejected by Bank" },
  MandateApprovedByBank: { value: 8, desc: "Mandate Approved by Bank" },
  MandateDisApprovedByBank: { value: 9, desc: "Mandate DisApproved by Bank" },
  NewMandateInitatedByBank: {
    value: 10,
    desc: "New Mandate Initiated by Bank",
  },
};

export const UserPreferenceEnum = {
  LANGUAGE: "language",
  THEME: "theme",
  FAVOURITE_ROUTES: "favourite_routes",
};

export const supervisor_screen_filter_action = ({ t, onClick, pageMode }) => {
  return [
    {
      key: "new",
      icon: <ExperimentOutlined style={{ fontSize: "14px" }} />,
      primary: false,
      label: t("common.Button.New"),
      disabled: false,
      onClick: onClick,
    },
    {
      key: "save",
      icon: <SaveOutlined style={{ fontSize: "14px" }} />,
      primary: pageMode === "NEW" || pageMode === "EDIT",
      isVisible: pageMode !== "LIST",
      label: t("Save"),
      onClick: onClick,
    },
  ];
};

export const EMPIRE_TRUST_BANK_CODE = "090114";

export const PORTAL_MODULE_ENUM = {
  LOANS: {
    value: 0,
    description: "Core_Loans",
  },
  TELLERING: {
    value: 1,
    description: "Core_Tellering",
  },
  DEPOSITS: {
    value: 2,
    description: "Core_Deposits",
  },
  INSURANCE: {
    value: 3,
    description: "Core_Insurance",
  },
  GENERAL: {
    value: 4,
    description: "General",
  },
  USER_MANAGEMENT: {
    value: 5,
    description: "Core_UserManagement",
  },
  CHANNELS: {
    value: 6,
    description: "Channels",
  },
  PLATFORM_ADMINISTRATION: {
    value: 7,
    description: "PlatformAdministration",
  },
  PORTAL_ADMINISTRATION: {
    value: 8,
    description: "PortalAdministration",
  },
  PORTAL_DEVELOPER: {
    value: 9,
    description: "PortalDeveloper",
  },
  AGENCY_BANKING: {
    value: 10,
    description: "AgencyBanking",
  },
  OPERATIONS: {
    value: 11,
    description: "CoreOperations",
  },
};

// SelfServiceProcessInstanceStatusEnum
export const SelfServiceProcessInstanceStatusEnum = {
  NOT_STARTED: 0,
  RUNNING: 1,
  WAITING: 2,
  CANCELLED: 3,
  COMPLETED: 4,
  EXPIRED: 5,
  ERROR: 6,
  UNKNOWN: 7,
};

export const ProcessInstanceStatusOptions = [
  {
    value: SelfServiceProcessInstanceStatusEnum.NOT_STARTED,
    label: "Not Started",
  },
  { value: SelfServiceProcessInstanceStatusEnum.RUNNING, label: "Running" },
  { value: SelfServiceProcessInstanceStatusEnum.WAITING, label: "Waiting" },
  { value: SelfServiceProcessInstanceStatusEnum.CANCELLED, label: "Cancelled" },
  { value: SelfServiceProcessInstanceStatusEnum.COMPLETED, label: "Completed" },
  { value: SelfServiceProcessInstanceStatusEnum.EXPIRED, label: "Expired" },
  { value: SelfServiceProcessInstanceStatusEnum.ERROR, label: "Error" },
  { value: SelfServiceProcessInstanceStatusEnum.UNKNOWN, label: "Unknown" },
];
